package com.mps.asc_app.ui.screens

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.ui.draw.clip
import asc_app.composeapp.generated.resources.logo_asc
import com.mps.asc_app.ui.theme.AscGoldLight
import org.jetbrains.compose.resources.painterResource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.Button
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import asc_app.composeapp.generated.resources.Res
import asc_app.composeapp.generated.resources.button_text_forgot_password
import asc_app.composeapp.generated.resources.button_text_login
import asc_app.composeapp.generated.resources.label_mail_adresse
import asc_app.composeapp.generated.resources.label_password
import asc_app.composeapp.generated.resources.placholder_password
import com.mps.asc_app.config.DebugRegistrationConfig
import com.mps.asc_app.presentation.MainViewModel
import com.mps.asc_app.ui.components.AppTextField
import org.jetbrains.compose.resources.stringResource

@Composable
fun LoginScreen(
    mainViewModel: MainViewModel,
    onLoginClick: (username: String, password: String) -> Unit,
    onForgotPasswordClick: () -> Unit,
    onRegisterClick: () -> Unit,
    onCreateDebugUserClick: (() -> Unit)? = null,
    onLoginWithTestUserClick: (() -> Unit)? = null,
    onLoginWithTestUserHasMemberNumberClick: (() -> Unit)? = null,
    contentPadding: PaddingValues = PaddingValues(0.dp)
) {
    val uiState by mainViewModel.uiState.collectAsState()

    LoginScreenContent(
        error = uiState.error,
        isLoading = uiState.isLoading,
        onLoginClick = onLoginClick,
        onForgotPasswordClick = onForgotPasswordClick,
        onRegisterClick = onRegisterClick,
        onCreateDebugUserClick = onCreateDebugUserClick,
        onLoginWithTestUserClick = onLoginWithTestUserClick,
        onLoginWithTestUserHasMemberNumberClick = onLoginWithTestUserHasMemberNumberClick,
        contentPadding = contentPadding,
    )
}

@Composable
fun LoginScreenContent(
    error: String? = null,
    isLoading: Boolean = false,
    onLoginClick: (username: String, password: String) -> Unit,
    onForgotPasswordClick: () -> Unit,
    onRegisterClick: () -> Unit,
    onCreateDebugUserClick: (() -> Unit)? = null,
    onLoginWithTestUserClick: (() -> Unit)? = null,
    onLoginWithTestUserHasMemberNumberClick: (() -> Unit)? = null,
    contentPadding: PaddingValues = PaddingValues(0.dp),
) {
    var username by rememberSaveable { mutableStateOf("") }
    var password by rememberSaveable { mutableStateOf("") }

    val usernameFocus = remember { FocusRequester() }
    val passwordFocus = remember { FocusRequester() }

    // Redesign Stufe 2: Vereinskopf auf Blau, darunter das Formular auf einem hellen Blatt.
    Column(
        modifier = Modifier
            .padding(contentPadding)
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.primary),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        // Der Vereinskopf scrollt nicht mit; sonst wurde das Logo beim Fokus aufs Formular oben abgeschnitten.
        Column(
            modifier = Modifier.padding(top = 24.dp, bottom = 24.dp, start = 24.dp, end = 24.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            Image(
                painter = painterResource(Res.drawable.logo_asc),
                contentDescription = null,
                modifier = Modifier
                    .size(88.dp)
                    .clip(CircleShape)
                    .border(3.dp, AscGoldLight, CircleShape),
            )
            Text(
                text = "ASC PADEL",
                style = MaterialTheme.typography.headlineMedium,
                color = MaterialTheme.colorScheme.onPrimary,
            )
            Text(
                text = "Plätze buchen · Mitspieler finden",
                style = MaterialTheme.typography.bodyMedium,
                color = AscGoldLight,
            )
        }
        // Bewusst Hintergrund statt Surface: eine nur oben abgerundete Surface schneidet zu und
        // verschluckte in den Robolectric-Tests die Klicks auf die Knöpfe.
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .weight(1f)
                .background(
                    MaterialTheme.colorScheme.background,
                    RoundedCornerShape(topStart = 28.dp, topEnd = 28.dp),
                ),
            contentAlignment = Alignment.TopCenter,
        ) {
            Column(
                modifier = Modifier
                    .widthIn(max = 460.dp)
                    .verticalScroll(rememberScrollState())
                    .padding(horizontal = 24.dp, vertical = 28.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                Text(
                    text = "Anmelden",
                    style = MaterialTheme.typography.headlineSmall,
                    color = MaterialTheme.colorScheme.primary,
                )

                if (!error.isNullOrBlank()) {
                    Surface(
                        color = MaterialTheme.colorScheme.errorContainer,
                        contentColor = MaterialTheme.colorScheme.onErrorContainer,
                        shape = MaterialTheme.shapes.medium,
                    ) {
                        Text(
                            text = error,
                            style = MaterialTheme.typography.bodySmall,
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(horizontal = 12.dp, vertical = 10.dp),
                            textAlign = TextAlign.Start,
                        )
                    }
                }

                Spacer(Modifier.height(2.dp))

                AppTextField(
                    value = username,
                    onValueChange = { username = it },
                    label = stringResource(Res.string.label_mail_adresse),
                    modifier = Modifier.fillMaxWidth(),
                    focusRequester = usernameFocus,
                    imeAction = ImeAction.Next,
                    onImeAction = { passwordFocus.requestFocus() }
                )

                AppTextField(
                    value = password,
                    onValueChange = { password = it },
                    label = stringResource(Res.string.label_password),
                    placeholder = stringResource(Res.string.placholder_password),
                    isPassword = true,
                    modifier = Modifier.fillMaxWidth(),
                    isError = error != null,
                    errorText = null,
                    focusRequester = passwordFocus,
                    imeAction = ImeAction.Done,
                    onImeAction = { onLoginClick(username, password) }
                )

                Spacer(Modifier.height(4.dp))

                Button(
                    onClick = { onLoginClick(username, password) },
                    modifier = Modifier.fillMaxWidth().heightIn(min = 52.dp),
                ) {
                    Text(stringResource(Res.string.button_text_login))
                }

                TextButton(
                    onClick = onForgotPasswordClick,
                    modifier = Modifier.fillMaxWidth(),
                    enabled = !isLoading,
                ) {
                    Text(stringResource(Res.string.button_text_forgot_password))
                }

                Text(
                    text = "Neu im Verein?",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.fillMaxWidth(),
                    textAlign = TextAlign.Center,
                )

                OutlinedButton(
                    onClick = onRegisterClick,
                    modifier = Modifier.fillMaxWidth().heightIn(min = 52.dp),
                ) {
                    Text("Registrieren")
                }

                if (DebugRegistrationConfig.enabled && onCreateDebugUserClick != null) {
                    OutlinedButton(
                        onClick = onCreateDebugUserClick,
                        modifier = Modifier.fillMaxWidth(),
                        enabled = !isLoading,
                    ) {
                        Text("Benutzer erstellen (Debug)")
                    }
                }

                if (DebugRegistrationConfig.enabled && onLoginWithTestUserClick != null) {
                    OutlinedButton(
                        onClick = onLoginWithTestUserClick,
                        modifier = Modifier.fillMaxWidth(),
                        enabled = !isLoading,
                    ) {
                        Text("Test: Geprüft, kein Mitglied (Debug)")
                    }
                }

                if (DebugRegistrationConfig.enabled && onLoginWithTestUserHasMemberNumberClick != null) {
                    OutlinedButton(
                        onClick = onLoginWithTestUserHasMemberNumberClick,
                        modifier = Modifier.fillMaxWidth(),
                        enabled = !isLoading,
                    ) {
                        Text("Test: Mitglied ohne Sparte (Debug)")
                    }
                }
            }
        }
    }
}
